import React, { ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Link } from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import { useTranslation } from 'react-i18next';
import { GridCellParams } from '@mui/x-data-grid';
import Toolbar from '../Toolbar';
import DataTable from '../DataTable';
import * as providerSlice from '../../../data/provider.slice';
import { SortModel } from '../../../data/models/sort-model.model';
import ProviderDialog from './ProviderDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import { FilterOption } from '../../FilterSelect';
import tableUtils from '../../../services/tableUtils';
import { useCanDeleteProvider } from '../../../hooks/permissions/useCanDeleteProvider';
import { useCanCreateProvider } from '../../../hooks/permissions/useCanCreateProvider';
import Tier3ProviderDialog from './Tier3ProviderDialog';
import { CoverageArea } from '../../../data/models/coverage-area.model';
import { ProviderPhoneNumber } from '../../../data/models/phone-number.model';
import phoneUtils, { ProviderPhoneUse } from '../../../services/phoneUtils';
import emailUtils, { ProviderEmailUse } from '../../../services/emailUtils';
import { selectTripModes } from '../../../data/reference.slice';

const PREFIX = 'Providers';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

export default function Providers() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const canDeleteProvider = useCanDeleteProvider();
  const canCreateProvider = useCanCreateProvider();

  const selectedFilter = useAppSelector(providerSlice.selectSelectedFilter);
  const searchFilter = useAppSelector(providerSlice.selectSearchFilter);
  const searchDriverIdFilter = useAppSelector(
    providerSlice.selectSearchDriverIdFilter,
  );
  const searchAreaFilter = useAppSelector(providerSlice.selectSearchAreaFilter);
  const page = useAppSelector(providerSlice.selectPage);
  const pageSize = useAppSelector(providerSlice.selectPageSize);
  const sortModel = useAppSelector(providerSlice.selectSortModel);
  const loading = useAppSelector(providerSlice.selectLoadingProviderList);
  const providerList = useAppSelector(providerSlice.selectProviderList);
  const provider = useAppSelector(providerSlice.selectProvider);
  const tripModes = useAppSelector(selectTripModes);

  const wheelchairTripModeIDs = React.useMemo(() => {
    const filterHasWheelchair = (selectedFilter || '')
      .split('&')
      .includes('svc0');
    if (!filterHasWheelchair || !tripModes?.length) {
      return undefined;
    }

    const lower = (s: string) => s?.toLowerCase?.() || '';
    const ids = tripModes
      .filter(
        (m) =>
          lower(m.tripModeName).includes('wheelchair') ||
          lower(m.tripModeBC).includes('wheelchair'),
      )
      .map((m) => m.tripModeID);
    return ids.length ? ids : undefined;
  }, [selectedFilter, tripModes]);

  const [editorOpen, setEditorOpen] = React.useState<boolean>(false);
  const [tier3EditorOpen, setTier3EditorOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    dispatch(
      providerSlice.fetchProviderListAsync(
        pageSize,
        page,
        selectedFilter,
        searchFilter,
        // searchDriverIdFilter,
        // searchAreaFilter,
        sortModel,
        wheelchairTripModeIDs,
      ),
    );
  }, [
    selectedFilter,
    searchFilter,
    searchDriverIdFilter,
    searchAreaFilter,
    page,
    pageSize,
    sortModel,
    wheelchairTripModeIDs,
    dispatch,
  ]);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState('');
  const [deleteId, setDeleteId] = React.useState(0);

  const renderProviderName = (params: GridCellParams): ReactElement => {
    return (
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          const isTier3 = params.row.transportationProviderTierID === 3;
          dispatch(providerSlice.setTabsetOneIndex(0));
          dispatch(providerSlice.setTabsetTwoIndex(0));
          const url = isTier3 ? '/providers/tier3/' : '/providers/';
          navigate(url + params.row.transportationProviderID);
        }}
        color="textPrimary"
        underline="hover">
        {params.row.transportationProviderName}
      </Link>
    );
  };

  const renderTier3ProviderName = (params: GridCellParams): ReactElement => {
    return (
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          navigate('/providers/tier3/' + params.row.transportationProviderID);
        }}
        color="textPrimary"
        underline="hover">
        {params.row.name}
      </Link>
    );
  };

  const renderDispatchEmail = (params: GridCellParams): React.ReactElement => {
    const email = emailUtils.getProviderEmail(
      params.row.transportationProviderEmails,
      ProviderEmailUse.Dispatch,
    );
    return <>{email?.emailAddress || ''}</>;
  };

  const renderDispatchPhone = (params: GridCellParams): React.ReactElement => {
    const phone = phoneUtils.getProviderPhone(
      params.row.transportationProviderPhones,
      ProviderPhoneUse.Dispatch,
    );
    return <>{phoneUtils.formatPhone(phone)}</>;
  };

  const renderMainPhone = (params: GridCellParams): React.ReactElement => {
    const phone = phoneUtils.getProviderPhone(
      params.row.transportationProviderPhones,
      ProviderPhoneUse.Main,
    );
    return <>{phoneUtils.formatPhone(phone)}</>;
  };

  const renderCoverageArea = (params: GridCellParams): React.ReactElement => {
    const coverageAreas = (params.row.transportationProviderCoverageAreas || [])
      .map((tpca: any) => tpca.coverageArea?.coverageAreaName)
      .filter(Boolean)
      .join(', ');
    return <>{coverageAreas}</>;
  };

  const deleteRow = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    params: GridCellParams,
  ) => {
    deleteProvider(
      evt,
      params.row.transportationProviderID,
      params.row.transportationProviderName,
    );
  };

  const deleteProvider = (evt: any, id: number, name: string) => {
    evt.preventDefault();
    setDeleteId(id);
    setConfirmMessage(t('providers.confirmDelete', { name }) || '');
    setConfirmOpen(true);
  };

  const confirmDeleteProvider = () => {
    setConfirmOpen(false);
    dispatch(providerSlice.deleteProviderAsync(deleteId));
  };

  const cancelDeleteProvider = () => {
    setConfirmOpen(false);
  };

  const columns = tableUtils.getProviderColumnDefinition(
    renderProviderName,
    renderDispatchEmail,
    renderDispatchPhone,
    renderMainPhone,
    renderCoverageArea,
    t,
    // NOTE: we removed delete provider for MVP
    canDeleteProvider ? deleteRow : null,
  );

  const onSearchChange = (val: string) => {
    dispatch(providerSlice.setSearchFilter(val));
  };

  const onSelectedFilterChange = (val: string) => {
    dispatch(providerSlice.setSelectedFilter(val));
  };

  const onPageChange = (pg: number) => {
    dispatch(providerSlice.setPage(pg));
  };

  const onPageSizeChange = (pgSize: number) => {
    dispatch(providerSlice.setPageSize(pgSize));
  };

  const onSortModelChange = (model: SortModel | null) => {
    dispatch(providerSlice.setSortModel(model));
  };

  const openProviderEditor = () => {
    setEditorOpen(true);
  };

  const openTier3ProviderEditor = () => {
    setTier3EditorOpen(true);
  };

  const closeProviderEditor = (saved: boolean) => {
    if (editorOpen && saved && provider) {
      navigate('/providers/' + provider.transportationProviderID);
    }
    setEditorOpen(false);
  };

  const closeTier3ProviderEditor = (saved: boolean) => {
    if (tier3EditorOpen && saved && provider) {
      navigate('/providers/tier3/' + provider.transportationProviderID);
    }
    setTier3EditorOpen(false);
  };

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setSearchParams({ tab: newValue.toString() });
  };

  const filterOptions: FilterOption[] = [
    { label: t('providers.filterStatus') },
    { label: t('providers.filterCTT'), value: 'ctt0' },
    { label: t('providers.filterNotCTT'), value: 'ctt1' },
    { label: t('providers.filterActiveState') },
    { label: t('providers.filterActive'), value: 'active0' },
    { label: t('providers.filterNotActive'), value: 'active1' },
    // The insurance filters are out for now because the new db schema makes it too hard
    // { label: t('providers.filterInsurance') },
    // { label: t('providers.filterInsuranceHigh'), value: 'ins0' },
    // { label: t('providers.filterInsuranceLow'), value: 'ins1' },
    { label: t('providers.filterServices') },
    { label: t('providers.filterWheelchair'), value: 'svc0' },
  ];

  const tier3FilterOptions: FilterOption[] = [
    { label: t('providers.filterActiveState') },
    { label: t('providers.filterActive'), value: 'active0' },
    { label: t('providers.filterNotActive'), value: 'active1' },
  ];

  return (
    <StyledBox className={classes.container}>
      <Toolbar
        button={canCreateProvider ? t('providers.add') : null}
        secondButton={canCreateProvider ? t('providers.addTier3') : null}
        filterOptions={filterOptions}
        currentFilter={selectedFilter}
        searchIcon={<EmojiTransportationIcon />}
        searchPlaceholder={t('providers.search')}
        searchAreaPlaceholder={t('providers.searchArea') || ''}
        currentSearchString={searchFilter}
        currentSearchDriverId={searchDriverIdFilter}
        currentSearchAreaString={searchAreaFilter}
        onFilterChange={onSelectedFilterChange}
        onSearchChange={onSearchChange}
        // onSearchAreaChange={onSearchAreaChange}
        onCreate={openProviderEditor}
        onSecondCreate={openTier3ProviderEditor}
        onImport={() => null}
      />
      <DataTable
        getRowId={(row: any) => row.transportationProviderID}
        columns={columns}
        itemList={providerList}
        pageSize={pageSize}
        sortModel={sortModel}
        loading={loading}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={onSortModelChange}
      />
      <ProviderDialog
        title={t('providers.create')}
        open={editorOpen}
        close={closeProviderEditor}
      />
      <Tier3ProviderDialog
        title={t('providers.tier3Create')}
        open={tier3EditorOpen}
        close={closeTier3ProviderEditor}
      />
      <ConfirmationDialog
        title={t('common.confirmDelete')}
        message={confirmMessage}
        open={confirmOpen}
        positiveAction={confirmDeleteProvider}
        negativeAction={cancelDeleteProvider}
      />
    </StyledBox>
  );
}
